import { createAxiosInstance } from "./axios";

/**
 * Service for Blackbird API calls
 * This API shares the same authentication token as the main auction API
 */

const blackbirdApiWithToken = createAxiosInstance(import.meta.env.VITE_BLACKBIRD_API, { withToken: true });
const blackbirdApiWithoutToken = createAxiosInstance(import.meta.env.VITE_BLACKBIRD_API, { withToken: false });

/**
 * Get item by id from Blackbird API
 * @param {string|number} itemId - The id of the item to get
 * @returns {Promise<Object>} Item details from Blackbird API
 */
export const getItemById = async (itemId) => {
  try {
    const { data } = await blackbirdApiWithToken.get(`/v1/Auction/GetItem?ID=${itemId}`);
    return data;
  } catch (error) {
    console.error("Error fetching item details from Blackbird:", error);
    throw error;
  }
};

/**
 * Get inspection PDF for an item
 * @param {string|number} itemId - The ID of the item
 * @returns {Promise<Blob>} PDF blob data
 */
export const getItemInspectionPDF = async (itemId) => {
  try {
    const response = await blackbirdApiWithoutToken.get(`/v1/Auction/GetItemInspectionPDFPublic?Item_ID=${itemId}`, {
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching inspection PDF:", error);
    throw error;
  }
};

/**
 * Get disclaimer settings (public, no auth required)
 * @returns {Promise<Object>} { ShowDisclaimer: bool, ShowDisclaimerContent: string }
 */
export const getDisclaimer = async () => {
  try {
    const { data } = await blackbirdApiWithoutToken.get(`/v1/Settings/GetDisclaimerPublic`);
    return data;
  } catch (error) {
    console.error("Error fetching disclaimer settings:", error);
    throw error;
  }
};

/**
 * Get the EnableOnlineAuction feature flag (public, no auth required)
 * @returns {Promise<boolean>} Whether online auctions are enabled
 */
export const getEnableOnlineAuction = async () => {
  try {
    const { data } = await blackbirdApiWithoutToken.get(`/v1/FeatureFlags/EnableOnlineAuction/public`);
    return data;
  } catch (error) {
    console.error("Error fetching EnableOnlineAuction flag:", error);
    throw error;
  }
};

/**
 * Get tenant Phenix access feature flag
 * @returns {Promise<boolean>} Boolean indicating if tenant has Phenix access
 */
export const getTenantPhenixAccess = async () => {
  try {
    const { data } = await blackbirdApiWithToken.get(`/v1/FeatureFlags/TenantPhenixAccess`);
    return data;
  } catch (error) {
    console.error("Error fetching tenant Phenix access:", error);
    throw error;
  }
};

/**
 * Add item to watchlist
 * @param {string|number} itemId - The ID of the item to add to watchlist
 * @returns {Promise<Object>} Response from the API
 */
export const addWatchListItem = async (itemId) => {
  try {
    const { data } = await blackbirdApiWithToken.post(`/v1/Auction/AddWatchList`, {
      Item_ID: itemId,
    });
    return data;
  } catch (error) {
    console.error("Error adding item to watchlist:", error);
    throw error;
  }
};

/**
 * Remove item from watchlist
 * @param {string|number} itemId - The ID of the item to remove from watchlist
 * @returns {Promise<Object>} Response from the API
 */
export const removeWatchListItem = async (itemId) => {
  try {
    const { data } = await blackbirdApiWithToken.put(`/v1/Auction/RemoveWatchList`, {
      Item_ID: itemId,
    });
    return data;
  } catch (error) {
    console.error("Error removing item from watchlist:", error);
    throw error;
  }
};

/**
 * Get watchlist items for the authenticated user
 * @returns {Promise<Object>} Watchlist items from the API
 */
export const getWatchList = async () => {
  try {
    const { data } = await blackbirdApiWithToken.get(`/v1/Auction/GetWatchList`);
    return data;
  } catch (error) {
    console.error("Error fetching watchlist:", error);
    throw error;
  }
};

/**
 * Request password reset for a user
 * This is a public endpoint that doesn't require authentication
 * Uses RequestPasswordResetUnified to support all frontend applications
 * @param {string} email - The email address of the user requesting password reset
 * @param {string} callbackURL - The callback URL to redirect to after password reset (default: "/login")
 * @param {number} entityId - The entity ID (default: 1, use 0 for internal/backoffice)
 * @param {string} frontendIdentifier - The frontend application identifier (default: "SimulcastAuction")
 * @returns {Promise<Object>} Response from the password reset request
 */
export const requestPasswordReset = async (
  email,
  callbackURL = "/login",
  entityId = 1,
  frontendIdentifier = "SimulcastAuction"
) => {
  try {
    const { data } = await blackbirdApiWithoutToken.post(`/v1/user/RequestPasswordResetUnified`, {
      Entity_ID: entityId,
      EmailAddress: email,
      CallbackURL: callbackURL,
      FrontendIdentifier: frontendIdentifier,
    });
    return data;
  } catch (error) {
    console.error("Error requesting password reset:", error);
    throw error;
  }
};

/**
 * Get contact info including company accounts for the authenticated user
 * @returns {Promise<Object>} Contact info including accounts array
 */
export const getContactInfo = async () => {
  try {
    const { data } = await blackbirdApiWithToken.get(`/v1/Auction/GetContactInfo`);
    return data;
  } catch (error) {
    console.error("Error fetching contact info:", error);
    throw error;
  }
};

/**
 * Reset password for a user using the reset token
 * This is a public endpoint that doesn't require authentication
 * @param {string} email - The email address of the user
 * @param {string} token - The reset token/code from the email
 * @param {string} password - The new password
 * @param {string} confirmPassword - Confirmation of the new password
 * @returns {Promise<Object>} Response from the password reset
 */
export const resetPassword = async (email, token, password, confirmPassword) => {
  try {
    const { data } = await blackbirdApiWithoutToken.post(`/v1/user/resetPassword`, {
      Email: email,
      Token: token,
      Password: password,
      Confirm: confirmPassword,
    });
    return data;
  } catch (error) {
    console.error("Error resetting password:", error);
    throw error;
  }
};
